"use client";

import { useState } from "react";
import Reveal from "./Reveal";

const details = [
  { icon: "📍", text: "123 Wellness Street, Green Valley, India" },
  { icon: "📞", text: "+91 98765 43210" },
  { icon: "✉️", text: "hello@ayurveda.com" },
  { icon: "🕒", text: "Mon – Sat: 9:00 AM – 7:00 PM" },
];

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      name: fd.get("name"),
      email: fd.get("email"),
      subject: fd.get("subject"),
      message: fd.get("message"),
    };
    setSending(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error ?? "Something went wrong. Please try again.");
        return;
      }
      setSent(true);
      form.reset();
      setTimeout(() => setSent(false), 5000);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-leaf-50 py-24">
      <div className="mx-auto grid max-w-6xl items-start gap-12 px-5 md:grid-cols-2">
        <Reveal>
          <span className="inline-block rounded-full bg-leaf-100 px-4 py-1.5 text-sm font-medium text-leaf-700">
            Contact Us
          </span>
          <h2 className="mt-5 font-display text-4xl font-bold leading-tight text-leaf-900 sm:text-5xl">
            Begin Your{" "}
            <span className="text-clay-500">Wellness Journey</span>
          </h2>
          <p className="mt-4 max-w-md text-leaf-800/80">
            Have a question or ready to book? Reach out and our team will get
            back to you soon.
          </p>

          <ul className="mt-8 space-y-4">
            {details.map((d) => (
              <li key={d.text} className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-lg shadow-sm">
                  {d.icon}
                </span>
                <span className="text-leaf-800">{d.text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex gap-3">
            {["f", "◎", "𝕏", "▶"].map((s, i) => (
              <a
                key={i}
                href="#"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-leaf-600 text-white transition-colors hover:bg-leaf-700"
              >
                {s}
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form
            onSubmit={onSubmit}
            className="rounded-3xl bg-white p-8 shadow-xl shadow-leaf-800/5"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                required
                name="name"
                placeholder="Your Name"
                className="rounded-xl border border-leaf-100 bg-sand-50 px-4 py-3 text-leaf-900 outline-none transition-colors placeholder:text-leaf-800/40 focus:border-leaf-500"
              />
              <input
                required
                name="email"
                type="email"
                placeholder="Your Email"
                className="rounded-xl border border-leaf-100 bg-sand-50 px-4 py-3 text-leaf-900 outline-none transition-colors placeholder:text-leaf-800/40 focus:border-leaf-500"
              />
            </div>
            <input
              name="subject"
              placeholder="Subject"
              className="mt-4 w-full rounded-xl border border-leaf-100 bg-sand-50 px-4 py-3 text-leaf-900 outline-none transition-colors placeholder:text-leaf-800/40 focus:border-leaf-500"
            />
            <textarea
              required
              name="message"
              rows={5}
              placeholder="Your Message"
              className="mt-4 w-full resize-none rounded-xl border border-leaf-100 bg-sand-50 px-4 py-3 text-leaf-900 outline-none transition-colors placeholder:text-leaf-800/40 focus:border-leaf-500"
            />
            <button
              type="submit"
              disabled={sending}
              className="mt-5 w-full rounded-full bg-leaf-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-leaf-600/30 transition-all hover:bg-leaf-700 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Message"}
            </button>
            {sent && (
              <p className="mt-4 text-center text-sm font-medium text-leaf-600">
                🌿 Thank you! Your message has been sent.
              </p>
            )}
            {error && (
              <p className="mt-4 text-center text-sm font-medium text-red-600">
                {error}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}
